import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import changelogEn from '../../../../CHANGELOG.en.md?raw'
import changelogDe from '../../../../CHANGELOG.md?raw'

/**
 * Minimal, dependency-free Markdown renderer for the CHANGELOG.
 *
 * Supports the subset of Markdown used in `CHANGELOG.md`: `#`/`##`/`###`
 * headings, `-` bullet lists (nested + wrapped continuation lines), fenced
 * ``` code blocks, paragraphs, inline `code` and `**bold**`. Kept dependency-
 * free so a single read-only view doesn't pull in a full markdown library.
 *
 * `##` release headings are rendered as `<h2>` with a localized dateline above
 * a prominent title, so the long document scans by date (its core job).
 */

/** German UI languages get the German changelog; everything else falls back to English. */
const GERMAN_LANGUAGES = new Set(['de', 'at', 'ch'])

/** Pick the changelog markdown matching the current UI language. */
export function useChangelogSource(): string {
  const { i18n } = useTranslation()
  const uiLanguage = (i18n.language || 'en').split('-')[0]?.toLowerCase() ?? 'en'
  return GERMAN_LANGUAGES.has(uiLanguage) ? changelogDe : changelogEn
}

/** One release, extracted from a `## YYYY-MM-DD — title` heading. `id` is the
 * anchor the release-index rail jumps to and scroll-spies on. */
export interface ChangelogRelease {
  id: string
  iso: string
  title: string
}

const RELEASE_HEADING = /^(\d{4}-\d{2}-\d{2})\s+—\s+([\s\S]*)$/

/** Stable, unique anchor id for a release. Two releases can share a date
 * (the changelog has multiple entries on 2026-05-07), so repeats get a
 * suffix — computed identically here and in the rendered heading so the rail
 * link and the anchor always agree. `counts` is mutated per document. */
function nextReleaseId(iso: string, counts: Map<string, number>): string {
  const n = (counts.get(iso) ?? 0) + 1
  counts.set(iso, n)
  return n === 1 ? `release-${iso}` : `release-${iso}-${n}`
}

/** Localize an ISO date for a release dateline (shared by heading + nav). */
export function formatReleaseDate(iso: string, locale: string): string {
  const parsed = new Date(`${iso}T00:00:00`)
  return Number.isNaN(parsed.getTime())
    ? iso
    : new Intl.DateTimeFormat(locale, { dateStyle: 'long' }).format(parsed)
}

/** Extract the dated releases (`## YYYY-MM-DD — title`) for the index rail —
 * fence-aware so a `## ` inside a code block is never mistaken for a release. */
export function parseReleases(source: string): ChangelogRelease[] {
  const releases: ChangelogRelease[] = []
  const counts = new Map<string, number>()
  let inCode = false
  for (const raw of source.split('\n')) {
    const line = raw.replace(/\s+$/, '')
    if (line.trim().startsWith('```')) {
      inCode = !inCode
      continue
    }
    if (inCode || !line.startsWith('## ')) continue
    const match = line.slice(3).match(RELEASE_HEADING)
    if (match?.[1] && match[2] != null) {
      releases.push({ id: nextReleaseId(match[1], counts), iso: match[1], title: match[2] })
    }
  }
  return releases
}

/** Render inline `code` and `**bold**` spans inside a single line of text. */
function renderInline(text: string): ReactNode[] {
  const nodes: ReactNode[] = []
  const regex = /(`[^`]+`|\*\*[^*]+\*\*)/g
  let lastIndex = 0
  let key = 0
  let match: RegExpExecArray | null
  // biome-ignore lint/suspicious/noAssignInExpressions: standard regex iteration
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index))
    }
    const token = match[0]
    if (token.startsWith('`')) {
      nodes.push(
        <code
          key={key++}
          className="rounded bg-muted px-1 py-0.5 font-mono text-[0.85em] text-foreground"
        >
          {token.slice(1, -1)}
        </code>
      )
    } else {
      nodes.push(<strong key={key++}>{token.slice(2, -2)}</strong>)
    }
    lastIndex = regex.lastIndex
  }
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex))
  }
  return nodes
}

interface ListItem {
  /** Leading-space count of the source line — drives nesting depth. */
  depth: number
  text: string
}

/** Render depth-tagged list items into properly nested `<ul>`s (indentation preserved). */
function NestedList({ items }: { items: ListItem[] }) {
  interface Node {
    text: string
    children: Node[]
  }
  const roots: Node[] = []
  const stack: Array<{ depth: number; node: Node }> = []
  for (const it of items) {
    const node: Node = { text: it.text, children: [] }
    while (stack.length > 0 && (stack[stack.length - 1]?.depth ?? -1) >= it.depth) stack.pop()
    const parent = stack[stack.length - 1]?.node
    if (parent) parent.children.push(node)
    else roots.push(node)
    stack.push({ depth: it.depth, node })
  }
  const renderNodes = (nodes: Node[]): ReactNode => (
    <ul className="my-2 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
      {nodes.map((n, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: static, non-reordered list
        <li key={i}>
          {renderInline(n.text)}
          {n.children.length > 0 && renderNodes(n.children)}
        </li>
      ))}
    </ul>
  )
  return renderNodes(roots)
}

/** Release header. Splits `YYYY-MM-DD — title` into a localized dateline above
 * a prominent title so releases stand out and scan by date. */
function ReleaseHeading({ text, locale, id }: { text: string; locale: string; id?: string }) {
  // scroll-mt-24: anchored jumps from the index rail land below the app's top bar.
  const headerClass =
    'scroll-mt-24 mt-8 mb-3 border-border border-t pt-5 first:mt-0 first:border-t-0 first:pt-0'
  const match = text.match(RELEASE_HEADING)
  if (!match) {
    return (
      <h2 className={`${headerClass} font-semibold text-foreground text-lg`}>
        {renderInline(text)}
      </h2>
    )
  }
  const iso = match[1] as string
  const title = match[2] as string
  return (
    <h2 id={id} className={headerClass}>
      <span className="block font-medium text-muted-foreground text-xs tabular-nums">
        {formatReleaseDate(iso, locale)}
      </span>
      <span className="mt-0.5 block font-semibold text-foreground text-lg">
        {renderInline(title)}
      </span>
    </h2>
  )
}

/** Render a CHANGELOG markdown string into React blocks. Prop-driven (no i18n
 * dependency) — the caller passes the UI locale for the release datelines. */
export function ChangelogMarkdown({ source, locale = 'en' }: { source: string; locale?: string }) {
  const lines = source.split('\n')
  const blocks: ReactNode[] = []
  let listItems: ListItem[] | null = null
  let paragraph: string[] | null = null
  let codeLines: string[] | null = null
  let key = 0
  // Same de-dup counter as parseReleases → anchor ids match the rail links.
  const releaseCounts = new Map<string, number>()

  const flushList = () => {
    if (listItems && listItems.length > 0) {
      blocks.push(<NestedList key={key++} items={listItems} />)
    }
    listItems = null
  }

  const flushParagraph = () => {
    if (paragraph) {
      const text = paragraph.join(' ')
      blocks.push(
        <p key={key++} className="my-2 text-sm text-muted-foreground">
          {renderInline(text)}
        </p>
      )
      paragraph = null
    }
  }

  const flushCode = () => {
    if (codeLines) {
      blocks.push(
        <pre
          key={key++}
          className="my-3 overflow-x-auto rounded-md bg-muted p-3"
          data-testid="changelog-code"
        >
          <code className="font-mono text-foreground text-xs">{codeLines.join('\n')}</code>
        </pre>
      )
      codeLines = null
    }
  }

  for (const raw of lines) {
    const line = raw.replace(/\s+$/, '')

    // Fenced code block: ``` toggles in/out. Content is rendered verbatim.
    if (line.trim().startsWith('```')) {
      if (codeLines === null) {
        flushList()
        flushParagraph()
        codeLines = []
      } else {
        flushCode()
      }
      continue
    }
    if (codeLines !== null) {
      codeLines.push(line)
      continue
    }

    if (!line.trim()) {
      flushList()
      flushParagraph()
      continue
    }
    if (line.startsWith('### ')) {
      flushList()
      flushParagraph()
      blocks.push(
        <h3 key={key++} className="mt-4 mb-1 font-semibold text-foreground text-sm">
          {renderInline(line.slice(4))}
        </h3>
      )
      continue
    }
    if (line.startsWith('## ')) {
      flushList()
      flushParagraph()
      const text = line.slice(3)
      const relMatch = text.match(RELEASE_HEADING)
      const id = relMatch?.[1] ? nextReleaseId(relMatch[1], releaseCounts) : undefined
      blocks.push(<ReleaseHeading key={key++} text={text} locale={locale} id={id} />)
      continue
    }
    if (line.startsWith('# ')) {
      // Top-level "# Changelog" title is shown via the card header instead.
      flushList()
      flushParagraph()
      continue
    }
    const listMatch = line.match(/^(\s*)-\s+(.*)$/)
    if (listMatch) {
      flushParagraph()
      if (!listItems) listItems = []
      listItems.push({ depth: (listMatch[1] ?? '').length, text: listMatch[2] ?? '' })
      continue
    }
    // Wrapped continuation line: append to the current list item or paragraph.
    if (listItems && listItems.length > 0) {
      const last = listItems[listItems.length - 1]
      if (last) last.text = `${last.text} ${line.trim()}`
      continue
    }
    if (!paragraph) paragraph = []
    paragraph.push(line.trim())
  }

  flushList()
  flushParagraph()
  flushCode()

  return <>{blocks}</>
}
